import React, { useState } from 'react';
import { Clock } from 'lucide-react';
import { ON_HOLD_REASONS } from './helpers';

export default function OnHoldReasonModal({ work, onClose, onSave }) {
  const existing = work.hold_reason || '';
  const existingNotes = work.hold_notes || '';
  const presets = ON_HOLD_REASONS.filter((r) => r !== 'Other');
  const [selected, setSelected] = useState(
    existing && presets.includes(existing) ? existing : existing ? 'Other' : ''
  );
  const [custom, setCustom] = useState(
    existing && !presets.includes(existing) ? existing : ''
  );
  const [notes, setNotes] = useState(existingNotes);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const reason = selected === 'Other' ? custom.trim() : selected;
    if (!reason) return;
    setSaving(true);
    try { await onSave(work, reason, notes.trim()); } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{ maxWidth: 380 }} data-testid="onhold-modal">
        <div className="modal-header">
          <h3 style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#92400e' }}>
            <Clock style={{ width: 16, height: 16 }} /> On Hold — Reason
          </h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <p style={{ fontSize: 12, color: '#78716c', marginBottom: 12 }}>
          A reason moves this work into the "On Hold" tab. Tap a reason to pick.
        </p>

        <div className="field">
          <label className="field-label">Reason</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-testid="reason-chips">
            {ON_HOLD_REASONS.map((r) => {
              const label = r === 'Other' ? 'Other (type below)' : r;
              const isSel = selected === r;
              return (
                <button key={r} type="button"
                  onClick={() => setSelected(r)}
                  style={{
                    background: isSel ? '#fef3c7' : '#fff',
                    border: `1.5px solid ${isSel ? '#d97706' : '#e7e5e4'}`,
                    color: isSel ? '#7c2d12' : '#1c1917',
                    padding: '11px 14px', borderRadius: 8, textAlign: 'left',
                    fontSize: 13, fontWeight: 700, cursor: 'pointer',
                    fontFamily: 'inherit', minHeight: 44, transition: 'all 0.15s',
                  }}
                  data-testid={`reason-${r.replace(/\s+/g, '-').toLowerCase()}`}>
                  {isSel ? '✓ ' : ''}{label}
                </button>
              );
            })}
          </div>
        </div>

        {selected === 'Other' && (
          <div className="field" style={{ marginTop: 10 }}>
            <label className="field-label">Custom Reason</label>
            <input className="field-input" value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="Type the reason…" autoFocus
              data-testid="reason-custom" />
          </div>
        )}

        {selected && (
          <div className="field" style={{ marginTop: 10 }}>
            <label className="field-label">Notes (optional)</label>
            <textarea className="field-input" value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3} placeholder="Add details — what's pending, who to follow up with, etc."
              data-testid="reason-notes" />
          </div>
        )}

        <div className="modal-actions">
          <button className="btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn-primary" onClick={save}
            disabled={saving || !selected || (selected === 'Other' && !custom.trim())}
            data-testid="save-reason-btn">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
